/**
 * Lenses scanner — reads a project's published prose files and projects them
 * into client-safe lens summaries.
 *
 * A "lens" is a prose file under `.appostle/brand/prose/photography/*.md`.
 * Files carrying a `lens:` block in their YAML frontmatter are renderable:
 * the block declares the client-facing schema (title, subject spec, variable
 * knobs). Files without the block are surfaced as locked/coming-soon entries.
 *
 * IP boundary: the prose body is the agency's deliverable and must NEVER
 * leave this module — only the declared frontmatter schema is returned.
 * Operator-internal knobs (`lens.internal`) are likewise stripped.
 */

import { readdir, readFile } from "node:fs/promises";
import { join } from "path";
import { parse as parseYaml } from "yaml";
import { LensRenderSchemaSchema, type LensSummary } from "../../shared/messages.js";

const PROSE_RELATIVE_DIR = join(".appostle", "brand", "prose", "photography");

/** Extracts the raw frontmatter block (between leading `---` fences), or null. */
function extractFrontmatter(source: string): string | null {
  if (!source.startsWith("---")) return null;
  const end = source.indexOf("\n---", 3);
  if (end === -1) return null;
  return source.slice(source.indexOf("\n") + 1, end);
}

function titleFromSlug(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export async function scanLenses(cwd: string): Promise<LensSummary[]> {
  const dir = join(cwd, PROSE_RELATIVE_DIR);
  let entries: string[];
  try {
    entries = await readdir(dir);
  } catch {
    return [];
  }

  const lenses: LensSummary[] = [];
  for (const entry of entries.filter((name) => name.endsWith(".md")).sort()) {
    const slug = entry.slice(0, -3);
    let raw: string;
    try {
      raw = await readFile(join(dir, entry), "utf8");
    } catch {
      continue;
    }

    let schema: LensSummary["schema"] = null;
    const frontmatter = extractFrontmatter(raw);
    if (frontmatter) {
      try {
        const data = parseYaml(frontmatter) as { lens?: unknown } | null;
        if (data && typeof data === "object" && data.lens) {
          const parsed = LensRenderSchemaSchema.safeParse(data.lens);
          if (parsed.success) {
            schema = parsed.data;
          }
        }
      } catch {
        // Malformed frontmatter → treat as schemaless (coming soon).
      }
    }

    const consumer: LensSummary["consumer"] =
      schema?.consumer === "nano" || (!schema && slug.endsWith("-nano")) ? "nano" : "mystic";

    lenses.push({
      slug,
      title: schema?.title ?? titleFromSlug(slug),
      description: schema?.description ?? null,
      consumer,
      schema,
    });
  }
  return lenses;
}
